/**
 * Basler as a camera vendor: their published range, and how to read their part
 * numbers. The choosing happens in ./cameras.ts, which knows nothing about
 * brands — this file supplies data to it.
 *
 * WHERE THIS COMES FROM, AND WHAT IT DELIBERATELY DOES NOT CONTAIN
 *
 * The model names below are Basler's own published list, from their product
 * documentation (docs.baslerweb.com/basler-ace-2, .../basler-racer-2). Nothing
 * here was taken from their shop: baslerweb.com/robots.txt disallows
 * `/shop/*` and the product-view-by-id pages, which is exactly where the
 * per-model pages live. So no page was crawled that asked not to be.
 *
 * That leaves a gap, and the gap is the interesting part: the specs are not
 * copied, they are DERIVED. Basler's part numbers are self-describing, so
 * resolution, speed, interface and mono/colour fall straight out of the name —
 * see decode() below. Facts derived from a documented naming convention, rather
 * than a mirror of someone else's pages that goes stale the week they revise it.
 *
 * I claimed here, in an earlier version of this comment, that Basler publishes no
 * prices. That was wrong, and wrong in an instructive way. Their landing pages
 * show none and robots.txt disallows `/shop/*`, from which I concluded the
 * prices must live somewhere I could not look. They are on the camera pages
 * themselves — rendered by JavaScript, so fetching the raw HTML returned a page
 * with no table and appeared to confirm the assumption instead of testing it.
 * All 225 net list prices are now recorded, by scripts/scrape-basler.mjs.
 *
 * Net list is not partner pricing. It is where a quote starts, not where margin
 * ends.
 *
 * One field stays absent: **sensor model and pixel size**, which the part number
 * does not encode. Left null rather than guessed — a wrong pixel size produces a
 * confident, wrong field-of-view calculation, which is worse than an empty field
 * that makes someone look it up.
 */
const DOCS_ACE2 = 'https://docs.baslerweb.com/basler-ace-2';
const DOCS_RACER2 = 'https://docs.baslerweb.com/basler-racer-2';
/**
 * Decode a Basler part number.
 *
 *   a2A2590-60umBAS  ->  ace 2, 2590 px wide, 60 fps, USB3, mono, BAS
 *   r2L16384-120cm   ->  racer 2, 16384 px/line, 120 kHz, CoaXPress, mono
 *
 * Returns null for anything it does not recognise rather than guessing — a
 * half-parsed part number that silently loses its interface would be picked by
 * the selector for a line it cannot serve.
 */
export function decode(model) {
    const ace = /^a2A(\d+)-(\d+)(g5|mg|g|u|c)(m|c)(BAS|PRO|IP67|SWIR|UV)?$/.exec(model);
    if (ace) {
        const [, width, speed, iface, colour, variant] = ace;
        const interfaces = {
            g: 'GigE',
            g5: '5GigE',
            u: 'USB3',
            c: 'CoaXPress',
            mg: 'GMSL2',
        };
        return {
            vendor: 'Basler',
            model,
            family: 'ace 2',
            scan: 'area',
            widthPx: Number(width),
            speed: Number(speed),
            speedUnit: 'fps',
            heightPx: null, // not encoded in the part number
            interface: interfaces[iface],
            colour: colour === 'm' ? 'mono' : 'colour',
            // IP67 is a housing rating, so it lives in the neutral field the washdown
            // filter reads. BAS/PRO is a Basler feature tier and stays a label.
            ingressProtection: variant === 'IP67' ? 'IP67' : null,
            // SWIR and UV are what the sensor sees, not how the camera is packaged, so
            // they are neither a housing rating nor a feature tier.
            spectrum: variant === 'SWIR' ? 'SWIR' : variant === 'UV' ? 'UV' : 'visible',
            tier: variant && !['IP67', 'SWIR', 'UV'].includes(variant) ? variant : null,
            // ace 2 ships both global and rolling shutter sensors and the part number
            // does not say which. Null rather than a guess: on a moving belt this
            // decides whether the image is usable, and Omron's table shows what
            // knowing it is worth.
            shutter: null,
            mount: null,
            sensor: null,
            pixelSizeUm: null,
            driver: 'basler',
            docsUrl: DOCS_ACE2,
        };
    }
    const racer = /^r2L(\d+)-(\d+)(g5|g|c)(m|c)$/.exec(model);
    if (racer) {
        const [, width, rate, iface, colour] = racer;
        const interfaces = { g: 'GigE', g5: '5GigE', c: 'CoaXPress' };
        return {
            vendor: 'Basler',
            model,
            family: 'racer 2',
            scan: 'line',
            widthPx: Number(width),
            speed: Number(rate),
            speedUnit: 'kHz',
            heightPx: null, // a line scan sensor has no height
            interface: interfaces[iface],
            colour: colour === 'm' ? 'mono' : 'colour',
            ingressProtection: null,
            spectrum: 'visible',
            tier: null,
            shutter: null,
            mount: null,
            sensor: null,
            pixelSizeUm: null,
            driver: 'basler',
            docsUrl: DOCS_RACER2,
        };
    }
    return null;
}
/**
 * Basler's published ace 2 range.
 *
 * Scraped from the product table at baslerweb.com/en/cameras/ace2/, all 18
 * pages of it. The previous list came from their documentation site and was 192
 * models; the published table holds 209.
 * Source: https://www.baslerweb.com/en/cameras/ace2/#products (2026-07-20)
 */
export const ACE2_MODELS = [
    'a2A1280-125umSWIR',
    'a2A1280-80gmSWIR',
    'a2A1920-160ucBAS',
    'a2A1920-160ucPRO',
    'a2A1920-160umBAS',
    'a2A1920-160umPRO',
    'a2A1920-165g5cBAS',
    'a2A1920-165g5mBAS',
    'a2A1920-168mgc',
    'a2A1920-168mgm',
    'a2A1920-51gcBAS',
    'a2A1920-51gcIP67',
    'a2A1920-51gcPRO',
    'a2A1920-51gmBAS',
    'a2A1920-51gmIP67',
    'a2A1920-51gmPRO',
    'a2A2048-110umSWIR',
    'a2A2048-114g5cBAS',
    'a2A2048-114g5mBAS',
    'a2A2048-114mgc',
    'a2A2048-114mgm',
    'a2A2048-114ucBAS',
    'a2A2048-114ucPRO',
    'a2A2048-114umBAS',
    'a2A2048-114umPRO',
    'a2A2048-173cmSWIR',
    'a2A2048-35gmSWIR',
    'a2A2048-37gcBAS',
    'a2A2048-37gcPRO',
    'a2A2048-37gmBAS',
    'a2A2048-37gmPRO',
    'a2A2440-98g5cBAS',
    'a2A2440-98g5mBAS',
    'a2A2448-105g5cBAS',
    'a2A2448-105g5mBAS',
    'a2A2448-120cc',
    'a2A2448-120cm',
    'a2A2448-210cc',
    'a2A2448-210cm',
    'a2A2448-23gcBAS',
    'a2A2448-23gcIP67',
    'a2A2448-23gcPRO',
    'a2A2448-23gmBAS',
    'a2A2448-23gmIP67',
    'a2A2448-23gmPRO',
    'a2A2448-75ucBAS',
    'a2A2448-75ucPRO',
    'a2A2448-75umBAS',
    'a2A2448-75umPRO',
    'a2A2448-90mgc',
    'a2A2448-90mgm',
    'a2A2464-115g5cBAS',
    'a2A2464-115g5mBAS',
    'a2A2464-23gcBAS',
    'a2A2464-23gcPRO',
    'a2A2464-23gmBAS',
    'a2A2464-23gmPRO',
    'a2A2464-77ucBAS',
    'a2A2464-77ucPRO',
    'a2A2464-77umBAS',
    'a2A2464-77umPRO',
    'a2A2560-131cmSWIR',
    'a2A2560-20gmSWIR',
    'a2A2560-70umSWIR',
    'a2A2590-22gcBAS',
    'a2A2590-22gcPRO',
    'a2A2590-22gmBAS',
    'a2A2590-22gmPRO',
    'a2A2590-60ucBAS',
    'a2A2590-60ucPRO',
    'a2A2590-60umBAS',
    'a2A2590-60umPRO',
    'a2A2600-20gcBAS',
    'a2A2600-20gcPRO',
    'a2A2600-20gmBAS',
    'a2A2600-20gmPRO',
    'a2A2600-64ucBAS',
    'a2A2600-64ucPRO',
    'a2A2600-64umBAS',
    'a2A2600-64umPRO',
    'a2A2840-14gcBAS',
    'a2A2840-14gcIP67',
    'a2A2840-14gcPRO',
    'a2A2840-14gmBAS',
    'a2A2840-14gmIP67',
    'a2A2840-14gmPRO',
    'a2A2840-14gmUV',
    'a2A2840-48ucBAS',
    'a2A2840-48ucPRO',
    'a2A2840-48umBAS',
    'a2A2840-48umPRO',
    'a2A2840-48umUV',
    'a2A2840-57mgc',
    'a2A2840-57mgm',
    'a2A2840-67g5cBAS',
    'a2A2840-67g5mBAS',
    'a2A2840-67g5mUV',
    'a2A2840-86cc',
    'a2A2840-86cm',
    'a2A3536-31ucBAS',
    'a2A3536-31ucPRO',
    'a2A3536-31umBAS',
    'a2A3536-31umPRO',
    'a2A3536-37mgc',
    'a2A3536-37mgm',
    'a2A3536-42g5cBAS',
    'a2A3536-42g5mBAS',
    'a2A3536-9gcBAS',
    'a2A3536-9gcPRO',
    'a2A3536-9gmBAS',
    'a2A3536-9gmPRO',
    'a2A3840-13gcBAS',
    'a2A3840-13gcPRO',
    'a2A3840-13gmBAS',
    'a2A3840-13gmPRO',
    'a2A3840-45ucBAS',
    'a2A3840-45ucPRO',
    'a2A3840-45umBAS',
    'a2A3840-45umPRO',
    'a2A4096-30ucBAS',
    'a2A4096-30ucPRO',
    'a2A4096-30umBAS',
    'a2A4096-30umPRO',
    'a2A4096-38mgc',
    'a2A4096-38mgm',
    'a2A4096-44g5cBAS',
    'a2A4096-44g5mBAS',
    'a2A4096-67cc',
    'a2A4096-67cm',
    'a2A4096-9gcBAS',
    'a2A4096-9gcIP67',
    'a2A4096-9gcPRO',
    'a2A4096-9gmBAS',
    'a2A4096-9gmIP67',
    'a2A4096-9gmPRO',
    'a2A4200-12gcBAS',
    'a2A4200-12gcPRO',
    'a2A4200-12gmBAS',
    'a2A4200-12gmPRO',
    'a2A4200-40ucBAS',
    'a2A4200-40ucPRO',
    'a2A4200-40umBAS',
    'a2A4200-40umPRO',
    'a2A4504-18ucBAS',
    'a2A4504-18ucPRO',
    'a2A4504-18umBAS',
    'a2A4504-18umPRO',
    'a2A4504-23mgc',
    'a2A4504-23mgm',
    'a2A4504-27g5cBAS',
    'a2A4504-27g5mBAS',
    'a2A4504-42cc',
    'a2A4504-42cm',
    'a2A4504-5gcBAS',
    'a2A4504-5gcIP67',
    'a2A4504-5gcPRO',
    'a2A4504-5gmBAS',
    'a2A4504-5gmIP67',
    'a2A4504-5gmPRO',
    'a2A4508-20ucBAS',
    'a2A4508-20ucPRO',
    'a2A4508-20umBAS',
    'a2A4508-20umPRO',
    'a2A4508-6gcBAS',
    'a2A4508-6gcPRO',
    'a2A4508-6gmBAS',
    'a2A4508-6gmPRO',
    'a2A5060-15ucBAS',
    'a2A5060-15umBAS',
    'a2A5060-21g5cBAS',
    'a2A5060-21g5mBAS',
    'a2A5060-35cc',
    'a2A5060-35cm',
    'a2A5060-4gcBAS',
    'a2A5060-4gmBAS',
    'a2A5320-23ucBAS',
    'a2A5320-23ucPRO',
    'a2A5320-23umBAS',
    'a2A5320-23umPRO',
    'a2A5320-29mgc',
    'a2A5320-29mgm',
    'a2A5320-34g5cBAS',
    'a2A5320-34g5mBAS',
    'a2A5320-52cc',
    'a2A5320-52cm',
    'a2A5320-7gcBAS',
    'a2A5320-7gcIP67',
    'a2A5320-7gcPRO',
    'a2A5320-7gmBAS',
    'a2A5320-7gmIP67',
    'a2A5320-7gmPRO',
    'a2A5328-15ucBAS',
    'a2A5328-15ucPRO',
    'a2A5328-15umBAS',
    'a2A5328-15umPRO',
    'a2A5328-19mgc',
    'a2A5328-19mgm',
    'a2A5328-22g5cBAS',
    'a2A5328-22g5mBAS',
    'a2A5328-35cc',
    'a2A5328-35cm',
    'a2A5328-4gcBAS',
    'a2A5328-4gcIP67',
    'a2A5328-4gcPRO',
    'a2A5328-4gmBAS',
    'a2A5328-4gmIP67',
    'a2A5328-4gmPRO',
    'a2A640-240gmSWIR',
    'a2A640-240umSWIR',
];
/**
 * Basler's published racer 2 range, all 16 models.
 *
 * The list before this one was assembled by pattern from a search summary and
 * was wrong in both directions — it missed seven models and, I believed,
 * invented nine. The nine were real; they were on page two of a pager I could
 * not drive, and I read 'I cannot see them' as 'they do not exist' and
 * deactivated them in the database. They are all here now, confirmed against
 * the table.
 * Source: https://www.baslerweb.com/en/cameras/racer2/#products (2026-07-20)
 */
export const RACER2_MODELS = [
    'r2L16384-120cm',
    'r2L16384-60cc',
    'r2L2048-172cm',
    'r2L2048-172g5m',
    'r2L2048-29gc',
    'r2L2048-58gm',
    'r2L2048-62cc',
    'r2L2048-62g5c',
    'r2L4096-14gc',
    'r2L4096-29gm',
    'r2L4096-42cc',
    'r2L4096-42g5c',
    'r2L4096-84cm',
    'r2L4096-84g5m',
    'r2L8192-240cm',
    'r2L8192-80cc',
];
/** Every model above, decoded. Anything that fails to parse is a bug, not a skip. */
export const BASLER_CAMERAS = [...ACE2_MODELS, ...RACER2_MODELS]
    .map(decode)
    .filter((c) => c !== null);
/**
 * Lenses and lighting.
 *
 * Basler sells both, but the useful thing to know before quoting is that they do
 * not MAKE the lenses — they resell Computar, Fujinon, Kowa, Ricoh, Evetar and
 * Schneider-Kreuznach. That is worth knowing when you are negotiating: the same
 * glass is available from those makers directly and from other distributors, so
 * a Basler lens line item is a convenience, not a lock-in.
 *
 * No part numbers here. Basler publishes lens and lighting order numbers only
 * through their selector tools and shop, both of which are off limits per their
 * robots.txt. Inventing plausible-looking ones would be worse than useless: a
 * fake order number on a real quote is a purchase order that bounces.
 * Sources: baslerweb.com/en/products/lenses/, /en/illumination/led-illumination/
 */
export const LENS_FAMILIES = [
    { mount: 'C-mount', makers: ['Computar', 'Fujinon', 'Kowa', 'Ricoh'], use: 'Standard area scan — the default for an ace 2 on a belt.' },
    { mount: 'S-mount', makers: ['Evetar'], use: 'Board-level cameras; compact, lower cost, less rigid.' },
    { mount: 'F-mount', makers: [], use: 'Large sensors where a C-mount cannot cover the image circle.' },
    { mount: 'Telecentric', makers: [], use: 'Dimensional measurement — no perspective error across the field. Available with coaxial light coupling.' },
    { mount: 'Line scan', makers: ['Schneider-Kreuznach'], use: 'For racer 2. Must cover the full line length.' },
];
export const LIGHT_TYPES = [
    { type: 'Bar', use: 'Long thin workpieces; grazing angle shows surface texture and dents.' },
    { type: 'Ring', use: 'General purpose, mounted around the lens. Even but flat — hides shallow defects.' },
    { type: 'Dome', use: 'Diffuse from all angles. For shiny or curved parts where a ring light would blow out.' },
    { type: 'Flat dome', use: 'Dome evenness in a shallow housing, for tight conveyor clearances.' },
    { type: 'Coaxial', use: 'Light down the optical axis — flat reflective surfaces, print and label inspection.' },
    { type: 'Backlight', use: 'Silhouette. Outline, shape and presence/absence, not surface.' },
    { type: 'Dark field', use: 'Low grazing angle so only edges and scratches light up.' },
    { type: 'Spot', use: 'Small concentrated area.' },
];
/**
 * Basler splits lighting into two lines, and the difference matters for wiring:
 * Premium is strobed by the camera itself over SLP (Strobe Light Protocol) via
 * pylon, so there is no separate trigger to wire or to get out of phase.
 * Standard has its own control and needs a trigger line from somewhere — which
 * on our lines means the HAL, and therefore a channel and a timing budget.
 */
export const LIGHTING_LINES = [
    { line: 'Premium', control: 'SLP — strobed directly by a compatible Basler camera through pylon.' },
    { line: 'Standard', control: 'Integrated control, continuous or triggered. Needs its own trigger source.' },
];
//# sourceMappingURL=basler.js.map